import type { ObstacleType } from '../../obstacles/ObstacleType';
import type { TerrainRow } from '../terrain/terrainAnalysis';
import { mergeRowLanes } from './SequenceValidator';

/** One decision line of a segment layout: terrain and obstacles combined (segment-local z). */
export interface LayoutRow {
  readonly z: number;
  readonly lanes: readonly (ObstacleType | null)[];
}

export interface LaneRequirement {
  readonly z: number;
  readonly lane: number;
  readonly type: ObstacleType;
}

/** Index of the terrain row an obstacle row at z belongs to (within the merge distance), or -1. */
export function terrainRowIndexAt(rows: readonly TerrainRow[], z: number, mergeDistance: number): number {
  let best = -1;
  let bestDistance = Infinity;
  rows.forEach((row, index) => {
    const distance = Math.abs(row.z - z);
    if (distance <= mergeDistance + 1e-6 && distance < bestDistance) {
      best = index;
      bestDistance = distance;
    }
  });
  return best;
}

/**
 * Combines terrain rows with obstacle requirements: obstacles on (or next to) a terrain
 * line join it, the others form their own rows. Sorted along the segment.
 */
export function buildLayoutRows(
  terrainRows: readonly TerrainRow[],
  requirements: readonly LaneRequirement[],
  laneCount: number,
  mergeDistance: number,
): LayoutRow[] {
  const rows = terrainRows.map((row) => ({ z: row.z, lanes: [...row.lanes] }));
  const byZ = new Map<number, (ObstacleType | null)[]>();
  for (const item of requirements) {
    const lanes = byZ.get(item.z) ?? Array.from({ length: laneCount }, () => null);
    lanes[item.lane] = item.type;
    byZ.set(item.z, lanes);
  }
  for (const [z, lanes] of byZ) {
    const index = terrainRowIndexAt(terrainRows, z, mergeDistance);
    const target = rows[index];
    if (target) target.lanes = mergeRowLanes(target.lanes, lanes);
    else rows.push({ z, lanes });
  }
  return rows.sort((a, b) => a.z - b.z);
}
